import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TableSkeleton from '../../Layout/TableSkeleton';
import styles from './Table.module.scss';
import TableCell from './TableCell';
import TableColumn from './TableColumn';
import { stickyColumnStyle, TableData, TableHeader } from './TableDefinitions';
import TableHeaderCell from './TableHeaderCell';

export interface ITableProps<T> {
    tableHeaders: TableHeader<T & { actions?: JSX.Element }>[];
    tableData: (TableData & T)[];
    minimumTableWidth?: number;
    maximumTableWidth?: number | string;
    maximumHeight?: number;
    displayDecimalPlaces?: number;
    tableStyle?: 'alternatingRows'; // TODO: Will add more options as we add the other tables.
    defaultHeaderOptions?: TableHeader<T & { actions?: JSX.Element }>;
    isScrollable?: boolean;
    includeTotalsRow?: boolean;
    isColumnOrderingEnabled?: boolean;
    hasGroupHeaders?: boolean;
    isLoading?: boolean;
}

const minimumHeaderHeight = 40;

// eslint-disable-next-line @typescript-eslint/comma-dangle
const Table = <T,>(props: ITableProps<T>) => {
    const [tallestHeaderHeight, setTallestHeaderHeight] = useState(minimumHeaderHeight);
    const [columnOrder, setColumnOrder] = useState<string[]>(() => props.tableHeaders.filter((h) => !h.hide).map((h) => String(h.field ?? h.header)));
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    useEffect(() => {
        setColumnOrder(props.tableHeaders.filter((h) => !h.hide).map((h) => String(h.field ?? h.header)));
    }, [props.tableHeaders]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveDragId(String(event.active.id));
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragId(null);
        if (over && active.id !== over.id) {
            setColumnOrder((current) => {
                const oldIndex = current.indexOf(String(active.id));
                const newIndex = current.indexOf(String(over.id));
                return arrayMove(current, oldIndex, newIndex);
            });
        }
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveDragId(null);
    }, []);

    // When column ordering is enabled, derive the rendered header list from columnOrder state.
    // When disabled, fall back to props.tableHeaders so the render path is identical to before.
    const orderedHeaders = useMemo(() => {
        if (!props.isColumnOrderingEnabled) {
            return props.tableHeaders;
        }
        return columnOrder
            .map((key) => props.tableHeaders.find((h) => String(h.field ?? h.header) === key))
            .filter((h): h is TableHeader<T & { actions?: JSX.Element }> => h !== undefined);
    }, [columnOrder, props.isColumnOrderingEnabled, props.tableHeaders]);

    const tableContent = (
        <div
            style={{
                minWidth: props.minimumTableWidth,
                maxWidth: props.maximumTableWidth,
                maxHeight: props.maximumHeight,
                overflow: props.isScrollable ? 'auto' : 'visible',
            }}
            className={styles.table}
        >
            {orderedHeaders
                .filter((x) => !x.hide)
                .map((x, tableHeaderIndex) => {
                    // When column ordering is enabled, orderedHeaders is already filtered so indices
                    // are consistent. When disabled, preserve the original props.tableHeaders reference
                    // for the pinned-right offset calc (matching prior behaviour exactly).
                    const headersForPinnedRightCalc = props.isColumnOrderingEnabled
                        ? (orderedHeaders.filter((h) => !h.hide) as TableHeader<T & { actions?: JSX.Element }>[])
                        : props.tableHeaders;

                    // Important: You should always provide a width for pinned children.
                    const childrenPinnedRightWidth = headersForPinnedRightCalc
                        .filter((_, indexOfCurrent) => tableHeaderIndex < indexOfCurrent)
                        .filter((y) => y.pinned === 'right')
                        .reduce((total, current) => total + (current.width ?? 0), 0);

                    const rightPinnedStyle = { ...stickyColumnStyle, right: childrenPinnedRightWidth };

                    if (x?.children) {
                        return (
                            <div
                                key={x.header ?? String(x.field)}
                                style={x.pinned === 'right' ? rightPinnedStyle : { position: 'relative' }}
                                className={styles.table_column}
                            >
                                <div className={styles.table_top_level_header}>
                                    <div className={styles.table_data_area}>
                                        <TableHeaderCell
                                            tableHeader={x}
                                            tallestHeaderHeight={tallestHeaderHeight}
                                            setTallestHeaderHeight={setTallestHeaderHeight}
                                            isColumnOrderingEnabled={props.isColumnOrderingEnabled}
                                            hasGroupHeaders={false}
                                        />
                                    </div>
                                </div>
                                <div className={styles.table_row}>
                                    {x.children
                                        .filter((y) => !y.hide)
                                        .filter((y) => y.pinned !== 'right')
                                        .map((child) => (
                                            <TableColumn
                                                key={String(child.field)}
                                                tableData={props.tableData}
                                                header={child}
                                                displayDecimalPlaces={props.displayDecimalPlaces}
                                                tableStyle={props.tableStyle}
                                                tallestHeaderHeight={tallestHeaderHeight}
                                                setTallestHeaderHeight={setTallestHeaderHeight}
                                                defaultHeaderOptions={props.defaultHeaderOptions}
                                                isColumnOrderingEnabled={props.isColumnOrderingEnabled}
                                                hasGroupHeaders={props.hasGroupHeaders}
                                            />
                                        ))}
                                </div>
                                <div className={styles.table_row}>
                                    {x.children
                                        .filter((y) => !y.hide)
                                        .filter((y) => y.pinned === 'right')
                                        .map((child) => (
                                            <TableColumn
                                                key={String(child.field)}
                                                tableData={props.tableData}
                                                header={child}
                                                displayDecimalPlaces={props.displayDecimalPlaces}
                                                tableStyle={props.tableStyle}
                                                defaultHeaderOptions={props.defaultHeaderOptions}
                                                tallestHeaderHeight={tallestHeaderHeight}
                                                setTallestHeaderHeight={setTallestHeaderHeight}
                                                isColumnOrderingEnabled={props.isColumnOrderingEnabled}
                                                hasGroupHeaders={props.hasGroupHeaders}
                                            />
                                        ))}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div key={x.header ?? String(x.field)} style={{ width: x.width, minWidth: x.minimumWidth }} className={styles.table_data_area}>
                            <TableColumn
                                tableData={props.tableData}
                                header={x}
                                displayDecimalPlaces={props.displayDecimalPlaces}
                                tableStyle={props.tableStyle}
                                defaultHeaderOptions={props.defaultHeaderOptions}
                                tallestHeaderHeight={tallestHeaderHeight}
                                setTallestHeaderHeight={setTallestHeaderHeight}
                                includeTotalsRow={props.includeTotalsRow}
                                isColumnOrderingEnabled={props.isColumnOrderingEnabled}
                                hasGroupHeaders={props.hasGroupHeaders}
                            />
                        </div>
                    );
                })}
        </div>
    );

    if (props.isLoading) {
        return <TableSkeleton numberOfColumns={props.tableHeaders.filter((x) => !x.hide).length} />;
    }

    if (!props.isColumnOrderingEnabled) {
        return tableContent;
    }

    const activeHeader = activeDragId ? orderedHeaders.find((h) => String(h.field ?? h.header) === activeDragId) : undefined;
    const activeHeaderDataFormatIsNumber = activeHeader?.dataFormat === 'currency' || activeHeader?.dataFormat === 'percent';

    return (
        <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                {tableContent}
            </SortableContext>
            <DragOverlay>
                {activeHeader ? (
                    <div className={styles.table_column} style={{ width: activeHeader.width, minWidth: activeHeader.minimumWidth }}>
                        <div
                            className={styles.table_header}
                            style={{
                                width: activeHeader.width,
                                minWidth: activeHeader.minimumWidth,
                                height: `${tallestHeaderHeight}px`,
                                justifyContent: activeHeaderDataFormatIsNumber ? 'end' : 'start',
                            }}
                        >
                            <div className={activeHeaderDataFormatIsNumber ? styles.table_header_number : styles.table_header_text}>
                                {activeHeader.headerValueGetter ? activeHeader.headerValueGetter() : activeHeader.header}
                            </div>
                        </div>
                        {props.tableData
                            .filter((row) => row.isVisible !== false)
                            .map((row, index) => (
                                <TableCell
                                    key={row.rowKey !== undefined ? String(row.rowKey) : index}
                                    tableDatum={row}
                                    header={activeHeader}
                                    datumIndex={index}
                                    displayDecimalPlaces={props.displayDecimalPlaces}
                                    tableStyle={props.tableStyle}
                                    defaultHeaderOptions={props.defaultHeaderOptions}
                                />
                            ))}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default Table;
